import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../lib/api";
import { wabaApi } from "../../lib/wabaApi";
import { useUnsavedGuard, blockedSave } from "./unsavedGuard";
import { ToolModal, TemplatePickerModal } from "./ConfigTools";
import { ExpressionEditor } from "../common/LazyExpressionEditor";
import { EscClose } from "../shared/useDialog";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { SavedNote } from "../shared/Icons";
import { backdropClose } from "../shared/backdrop";
import { askDiscard } from "../shared/DiscardPrompt";
import { ModelSelector, type ModelSelectorValue } from "./ModelSelector";
import { ModelFallbackSection, fallbackToForm, formToFallback } from "./ModelFallbackSection";
import { channelOf, type Channel } from "./ConfigPrompt";
import { bodyOf, countSlots } from "../../lib/wabaTemplates";

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-secondary)",
  display: "block",
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box" as const,
};

const DOW = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

// Blocos de follow (followup.steps). after_minutes = inatividade ACUMULADA; unit só exibição.
const MAX_FOLLOWS = 10;
type Unit = "min" | "h";
type CardKind = "template" | "prompt";
// Fixo = string com {{ vars }}; Agente = { ai: prompt } — o agente preenche pelo histórico.
type TplParam = string | { ai: string };
type Tpl = { name: string; language: string; params: TplParam[] };
type Slot = { value: string; unit: Unit; template: Tpl | null; prompt: string };
const toSlot = (s: any): Slot => {
  const unit: Unit = s?.unit === "h" ? "h" : "min";
  const m = Number(s?.after_minutes) || 0;
  return { value: String(unit === "h" ? m / 60 : m), unit, template: s?.template ?? null, prompt: s?.prompt ?? "" };
};
const slotMinutes = (s: Slot) => Math.round((parseFloat(s.value) || 0) * (s.unit === "h" ? 60 : 1));
const fmtMinutes = (m: number) => (m % 60 === 0 ? `${m / 60}h` : `${m} min`);

export function ConfigFollowup({ agent, channel = "whatsapp" }: { agent: any; channel?: Channel }) {
  const qc = useQueryClient();
  const ch = channelOf(channel);
  const isWa = channel === "whatsapp";  // template só existe no WhatsApp
  const fu = agent[ch.followup] ?? {};
  const et = fu.end_tool ?? {};
  const sched = fu.schedule ?? {};

  // end_tool é uma linha real na tabela tools com role='followup_end' — do canal desta sub-aba.
  const { data: endTools = [] } = useQuery<any[]>({
    queryKey: ["agent-tools", agent.id, "followup_end", channel],
    queryFn: () => api.get(`/agents/${agent.id}/tools?role=followup_end&channel=${channel}`),
  });
  const endTool = endTools[0] ?? null;

  const [enabled, setEnabled] = useState<boolean>(fu.enabled ?? false);
  const [slots, setSlots] = useState<Slot[]>((Array.isArray(fu.steps) ? fu.steps : []).map(toSlot));
  const [cronInterval, setCronInterval] = useState<string>(String(fu.cron_interval_minutes ?? "30"));
  const [sendSpacing, setSendSpacing] = useState<string>(String(fu.send_spacing_seconds ?? "8"));
  const [finalizeClosed, setFinalizeClosed] = useState<boolean>(!!fu.finalize_closed_window);
  const [prompt, setPrompt] = useState<string>(fu.prompt ?? "");
  const [provider, setProvider] = useState<string>(fu.model_provider ?? agent.model_provider ?? "openai");
  const [modelName, setModelName] = useState<string>(fu.model_name ?? "");
  const [temperature, setTemperature] = useState<number>(fu.temperature ?? 0.6);
  const [reasoningEffort, setReasoningEffort] = useState<string>(fu.reasoning_effort ?? "off");
  const [verbosity, setVerbosity] = useState<string>(fu.verbosity ?? "default");
  const [maxTokens, setMaxTokens] = useState<number>(fu.max_tokens ?? 4096);
  const [fallback, setFallback] = useState<ModelSelectorValue | null>(fallbackToForm(fu.model_fallback));

  const [scheduleEnabled, setScheduleEnabled] = useState<boolean>(sched.enabled ?? false);
  const [scheduleDays, setScheduleDays] = useState<number[]>(
    Array.isArray(sched.days) ? sched.days : [0, 1, 2, 3, 4, 5, 6]
  );
  const [scheduleStart, setScheduleStart] = useState<string>(sched.start ?? "07:00");
  const [scheduleEnd, setScheduleEnd] = useState<string>(sched.end ?? "21:30");

  const [endToolEnabled, setEndToolEnabled] = useState<boolean>(!!et.enabled);
  const [endToolPicker, setEndToolPicker] = useState(false);

  // Template WABA por follow: bloco com template sempre envia ele (sem IA, qualquer janela). Lista vem da conexão do agente.
  const { data: bound } = useQuery<any | null>({
    queryKey: ["waba-binding", agent.id],
    queryFn: () => wabaApi.get(`/bindings/agent/${agent.id}`),
    enabled: isWa,
  });
  const { data: wabaTemplates = [] } = useQuery<any[]>({
    queryKey: ["waba-templates", bound?.id],
    queryFn: () => wabaApi.get(`/connections/${bound.id}/templates`),
    enabled: !!bound?.id,
  });
  const approved = wabaTemplates.filter((t) => t.status === "APPROVED");

  // Blocos: tempo fica no slot (posição); template e prompt extra são cartões
  // que arrastam entre slots (soltar sobre outro do mesmo tipo = troca).
  const [editing, setEditing] = useState<{ kind: CardKind; i: number } | null>(null);
  const [drag, setDrag] = useState<{ kind: CardKind; from: number } | null>(null);
  const [overSlot, setOverSlot] = useState<number | null>(null);
  const patchSlot = (i: number, p: Partial<Slot>) => setSlots((prev) => prev.map((s, j) => (j === i ? { ...s, ...p } : s)));
  function moveCard(kind: CardKind, a: number, b: number) {
    if (a === b) return;
    setSlots((prev) => {
      const n = prev.map((s) => ({ ...s }));
      if (kind === "template") [n[a].template, n[b].template] = [n[b].template, n[a].template];
      else [n[a].prompt, n[b].prompt] = [n[b].prompt, n[a].prompt];
      return n;
    });
  }
  function addSlot() {
    const last = slots.length ? slotMinutes(slots[slots.length - 1]) : 0;
    setSlots([...slots, { value: String(last + 60), unit: "min", template: null, prompt: "" }]);
  }
  const mins = slots.map(slotMinutes);
  const slotError = (i: number) =>
    mins[i] < 1 ? "Informe um tempo maior que zero."
      : i > 0 && mins[i] <= mins[i - 1] ? `Precisa ser depois do Follow ${i} (${fmtMinutes(mins[i - 1])}).`
      : null;

  const [endToolCreating, setEndToolCreating] = useState<{ kind: "http" | "python"; step: "curl" | "form" } | null>(null);
  const [endToolImporting, setEndToolImporting] = useState(false);
  const [endToolCopying, setEndToolCopying] = useState(false);
  const [endToolEditing, setEndToolEditing] = useState(false);
  const [confirmDelEndTool, setConfirmDelEndTool] = useState(false);
  const deleteEndToolMut = useMutation({
    mutationFn: () => api.delete(`/agents/${agent.id}/tools/${endTool.id}`),
    onSuccess: () => {
      setEndToolEnabled(false);
      setConfirmDelEndTool(false);
      qc.invalidateQueries({ queryKey: ["agent-tools", agent.id, "followup_end"] });
    },
  });

  // Tools normais do canal — só carrega quando o modal "usar existente" abre.
  const { data: agentTools = [] } = useQuery<any[]>({
    queryKey: ["agent-tools", agent.id, "agent", channel],
    queryFn: () => api.get(`/agents/${agent.id}/tools?channel=${channel}`),
    enabled: endToolCopying,
  });
  const copyEndToolMut = useMutation({
    mutationFn: (toolId: string) =>
      api.post(`/agents/${agent.id}/tools/${toolId}/duplicate`, { role: "followup_end", enabled: true }),
    onSuccess: () => {
      setEndToolCopying(false);
      setEndToolEnabled(true);
      qc.invalidateQueries({ queryKey: ["agent-tools", agent.id, "followup_end"] });
    },
  });

  // Vale mesmo desligado: o payload sempre leva os blocos (after_minutes < 1 = 422).
  const spacingsInvalid = slots.some((_, i) => slotError(i));

  const current = JSON.stringify({ enabled, slots, cronInterval, sendSpacing, prompt, provider, modelName, temperature, reasoningEffort, verbosity, maxTokens, fallback, scheduleEnabled, scheduleDays, scheduleStart, scheduleEnd, endToolEnabled, finalizeClosed });
  const [savedSnap, setSavedSnap] = useState(current);

  const mut = useMutation({
    mutationFn: () =>
      api.patch(`/agents/${agent.id}`, {
        [ch.followup]: {
          enabled,
          steps: slots.map((s) => ({
            after_minutes: slotMinutes(s),
            unit: s.unit,
            template: isWa ? s.template : null,
            prompt: s.prompt,
          })),
          cron_interval_minutes: parseInt(cronInterval) || 30,
          send_spacing_seconds: parseInt(sendSpacing) || 0,
          finalize_closed_window: finalizeClosed,
          prompt,
          model_provider: provider,
          model_name: modelName,
          temperature,
          reasoning_effort: reasoningEffort === "off" ? null : reasoningEffort,
          verbosity: verbosity === "default" ? null : verbosity,
          max_tokens: maxTokens,
          model_fallback: formToFallback(fallback),
          schedule: {
            enabled: scheduleEnabled,
            days: scheduleDays,
            start: scheduleStart,
            end: scheduleEnd,
          },
          end_tool: {
            enabled: endToolEnabled,
            tool_id: endTool?.id ?? null,
          },
        },
      }),
    onSuccess: () => {
      // Reusa o próprio `current`: duplicar a lista de campos aqui já deixou o
      // snapshot p/ trás — salvo continuava "não salvo".
      setSavedSnap(current);
      qc.invalidateQueries({ queryKey: ["agent", agent.id] });
    },
  });

  useUnsavedGuard(current !== savedSnap, spacingsInvalid ? blockedSave : mut.mutateAsync);

  const hasEndTool = !!endTool;

  return (
    <div style={{ maxWidth: 520, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Config sempre visível; o estado ligado/desligado fica na cor do bloco. */}
      <label htmlFor="followup-enabled" style={{
        display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 10, cursor: "pointer",
        border: `1px solid ${enabled ? "var(--ok-line)" : "var(--danger-soft-line)"}`,
        background: enabled ? "var(--ok-soft)" : "var(--danger-soft)",
        transition: "background .15s, border-color .15s",
      }}>
        <input
          type="checkbox"
          id="followup-enabled"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          style={{ accentColor: enabled ? "var(--ok)" : "var(--brand)", width: 16, height: 16 }}
        />
        <span style={{ fontSize: 13, color: "var(--ink)" }}>Habilitar follow-up automático</span>
        <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 600, color: enabled ? "var(--ok)" : "var(--danger)" }}>
          {enabled ? "Ligado" : "Desligado · nenhum follow é enviado"}
        </span>
      </label>

      <>
          {/* ── Blocos de follow ─────────────────────────────────── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", margin: 0 }}>Follows</h4>
              <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>{slots.length}/{MAX_FOLLOWS}</span>
            </div>
            <span style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: -4, lineHeight: 1.5 }}>
              Tempo = inatividade total do lead desde a última interação (o sistema desconta o follow anterior).
              {isWa
                ? " Bloco com template: sempre envia o template, com a janela aberta ou fechada. Sem template: a IA escreve pelo prompt do follow-up (no fim da aba) + o prompt extra do bloco; com a janela fechada o follow conta como consumido sem envio (ou finaliza o lead, se ligado abaixo)."
                : ` ${ch.label}: janela de 24h (até 7 dias se veio de anúncio). Fechada, o follow não é enviado — conta como consumido e aparece como aviso na conversa (ou finaliza o lead, se ligado abaixo).`}
              {" "}Arraste os cartões entre os follows para trocar o conteúdo.
            </span>

            {slots.map((s, i) => {
              const err = slotError(i);
              const isOver = overSlot === i && drag && drag.from !== i;
              return (
                <div key={i}
                  onDragOver={(e) => { if (drag) { e.preventDefault(); setOverSlot(i); } }}
                  onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOverSlot(null); }}
                  onDrop={(e) => { e.preventDefault(); if (drag) moveCard(drag.kind, drag.from, i); setDrag(null); setOverSlot(null); }}
                  style={{
                    border: `1px solid ${err ? "var(--danger)" : isOver ? "var(--brand)" : "var(--line-strong)"}`,
                    background: isOver ? "var(--brand-soft)" : "var(--surface-card)",
                    borderRadius: 10, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10,
                    transition: "border-color .12s, background .12s",
                  }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, color: "var(--brand)", background: "var(--brand-soft)",
                      border: "1px solid var(--brand-line)", borderRadius: 999, padding: "2px 10px", whiteSpace: "nowrap",
                    }}>
                      Follow {i + 1}
                    </span>
                    <span style={{ fontSize: 12, color: "var(--ink-secondary)" }}>Após</span>
                    <input type="number" min={1} step="any" value={s.value}
                      onChange={(e) => patchSlot(i, { value: e.target.value })}
                      style={{ ...inputStyle, width: 76, padding: "5px 8px", fontSize: 12 }} />
                    <select value={s.unit} onChange={(e) => {
                        // Troca de unidade converte o número (60 min → 1 h), não reinterpreta.
                        const unit = e.target.value as Unit;
                        patchSlot(i, { unit, value: String(+(slotMinutes(s) / (unit === "h" ? 60 : 1)).toFixed(2)) });
                      }}
                      style={{ ...inputStyle, width: "auto", padding: "5px 8px", fontSize: 12 }}>
                      <option value="min">minutos</option>
                      <option value="h">horas</option>
                    </select>
                    <span style={{ fontSize: 12, color: "var(--ink-secondary)" }}>de inatividade</span>
                    <button type="button" title="Remover follow" aria-label={`Remover Follow ${i + 1}`}
                      onClick={() => { setSlots(slots.filter((_, j) => j !== i)); }}
                      style={{ marginLeft: "auto", fontSize: 12, padding: "3px 9px", borderRadius: 6, border: "1px solid var(--line-strong)", background: "transparent", color: "var(--ink-muted)", cursor: "pointer" }}>
                      Remover
                    </button>
                  </div>
                  {err && <span style={{ fontSize: 11, color: "var(--danger)", marginTop: -4 }}>{err}</span>}

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {isWa && (s.template ? (
                      <FollowCard label="Template" text={s.template.name}
                        onOpen={() => setEditing({ kind: "template", i })}
                        onDragStart={() => setDrag({ kind: "template", from: i })}
                        onDragEnd={() => { setDrag(null); setOverSlot(null); }} />
                    ) : (
                      <AddCardButton onClick={() => setEditing({ kind: "template", i })}>+ Template</AddCardButton>
                    ))}
                    {s.prompt.trim() ? (
                      <FollowCard label="Prompt extra" text={s.prompt.trim().split("\n")[0]}
                        onOpen={() => setEditing({ kind: "prompt", i })}
                        onDragStart={() => setDrag({ kind: "prompt", from: i })}
                        onDragEnd={() => { setDrag(null); setOverSlot(null); }} />
                    ) : (
                      <AddCardButton onClick={() => setEditing({ kind: "prompt", i })}>+ Prompt extra</AddCardButton>
                    )}
                  </div>
                </div>
              );
            })}

            <button type="button" onClick={addSlot} disabled={slots.length >= MAX_FOLLOWS}
              style={{
                padding: "10px 0", borderRadius: 10, border: "1px dashed var(--line-strong)", background: "transparent",
                color: slots.length >= MAX_FOLLOWS ? "var(--ink-dim)" : "var(--ink-secondary)", fontSize: 13,
                cursor: slots.length >= MAX_FOLLOWS ? "not-allowed" : "pointer",
              }}>
              {slots.length >= MAX_FOLLOWS ? `Máximo de ${MAX_FOLLOWS} follows` : "+ Adicionar follow"}
            </button>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <label style={labelStyle}>Verificar a cada (min)</label>
              <input type="number" style={inputStyle} value={cronInterval} min={1} max={1440}
                onChange={(e) => setCronInterval(e.target.value)} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <label style={labelStyle}>Espaço entre envios (s)</label>
              <input type="number" style={inputStyle} value={sendSpacing} min={0} max={300}
                onChange={(e) => setSendSpacing(e.target.value)} />
            </div>
          </div>

          {!agent.template_id && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 480 }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", margin: 0 }}>
              Modelo principal
            </h4>
            <ModelSelector
              showMaxTokens
              showVerbosity
              value={{ provider, model_name: modelName, temperature, reasoning_effort: reasoningEffort, verbosity, maxTokens }}
              onChange={(next) => {
                setProvider(next.provider);
                setModelName(next.model_name);
                setTemperature(next.temperature);
                setReasoningEffort(next.reasoning_effort);
                setVerbosity(next.verbosity ?? "default");
                setMaxTokens(next.maxTokens ?? 4096);
              }}
            />
            <ModelFallbackSection
              showVerbosity
              value={fallback}
              onChange={setFallback}
              hint="Usado se o modelo do follow-up falhar. Sem modelo próprio acima, o follow-up herda o fallback do agente."
            />
          </div>
          )}

          {/* ── Restrição de horário ─────────────────────────────── */}
          <div style={{ border: "1px solid var(--line-strong)", borderRadius: 8, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input type="checkbox" id="schedule-enabled" checked={scheduleEnabled}
                onChange={(e) => setScheduleEnabled(e.target.checked)}
                style={{ accentColor: "var(--brand)", width: 16, height: 16 }} />
              <label htmlFor="schedule-enabled" style={{ fontSize: 13, color: "var(--ink-secondary)", cursor: "pointer" }}>
                Restringir horário (fora dele não roda nem gasta execução)
              </label>
            </div>
            {scheduleEnabled && (
              <>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {DOW.map((d, i) => {
                    const on = scheduleDays.includes(i);
                    return (
                      <button key={i} type="button"
                        onClick={() => setScheduleDays(on ? scheduleDays.filter((x) => x !== i) : [...scheduleDays, i].sort((a, b) => a - b))}
                        style={{
                          fontSize: 12, padding: "5px 10px", borderRadius: 6, cursor: "pointer",
                          border: `1px solid ${on ? "var(--brand)" : "var(--line-strong)"}`,
                          background: on ? "rgba(195,32,32,0.12)" : "var(--surface-hover)",
                          color: on ? "var(--brand)" : "var(--ink-soft)",
                        }}>
                        {d}
                      </button>
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    <label style={labelStyle}>Primeiro horário</label>
                    <input type="time" style={inputStyle} value={scheduleStart}
                      onChange={(e) => setScheduleStart(e.target.value)} />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                    <label style={labelStyle}>Último horário</label>
                    <input type="time" style={inputStyle} value={scheduleEnd}
                      onChange={(e) => setScheduleEnd(e.target.value)} />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ── Janela fechada sem template ──────────────────────── */}
          <div style={{ border: "1px solid var(--line-strong)", borderRadius: 8, padding: 14, display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input type="checkbox" id="finalize-closed-window" checked={finalizeClosed}
                onChange={(e) => setFinalizeClosed(e.target.checked)}
                style={{ accentColor: "var(--brand)", width: 16, height: 16 }} />
              <label htmlFor="finalize-closed-window" style={{ fontSize: 13, color: "var(--ink-secondary)", cursor: "pointer" }}>
                Finalizar o lead com janela fechada
              </label>
            </div>
            <span style={{ fontSize: 11, color: "var(--ink-dim)", lineHeight: 1.5 }}>
              Só em follow sem template. Ligado: com a janela fechada o lead é finalizado (motivo "janela fechada") e segue a finalização normal, chamando a tool de encerramento se cadastrada.
            </span>
          </div>

          {/* ── Tool de encerramento ─────────────────────────────── */}
          <div style={{ border: "1px solid var(--line-strong)", borderRadius: 8, padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" id="end-tool-enabled" checked={endToolEnabled}
                  onChange={(e) => setEndToolEnabled(e.target.checked)}
                  style={{ accentColor: "var(--brand)", width: 16, height: 16 }} />
                <label htmlFor="end-tool-enabled" style={{ fontSize: 13, color: "var(--ink-secondary)", cursor: "pointer" }}>
                  Tool de encerramento
                </label>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {hasEndTool && (
                  <button onClick={() => setConfirmDelEndTool(true)} disabled={deleteEndToolMut.isPending}
                    style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "1px solid var(--brand-line)", background: "var(--surface-hover)", color: "var(--danger)", cursor: "pointer" }}>
                    {deleteEndToolMut.isPending ? "Excluindo..." : "Excluir"}
                  </button>
                )}
                <button onClick={() => { if (hasEndTool) setEndToolEditing(true); else setEndToolPicker(true); }}
                  style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "1px solid var(--line-strong)", background: "var(--surface-hover)", color: "var(--ink-secondary)", cursor: "pointer" }}>
                  {hasEndTool ? "Editar" : "+ Configurar"}
                </button>
              </div>
            </div>
            <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>
              O agente preenche os parâmetros (inclua um campo de resumo no schema) e chama esta tool ao encerrar. Sem ela, o encerramento é silencioso.
            </span>
            {hasEndTool && (
              <div style={{ background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 6, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{endTool.name}</span>
                {endTool.description && <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>{endTool.description}</span>}
                <span style={{ fontSize: 11, color: "var(--line-max)", fontFamily: "var(--font-mono)" }}>
                  {endTool.kind === "python" ? "Python" : `${endTool.method ?? ""} ${endTool.url ?? ""}`}
                </span>
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={labelStyle}>Prompt do agente de follow-up</label>
            <ExpressionEditor value={prompt} onChange={setPrompt} autoGrow minHeight={180} maxHeight={480} highlightTags />
            <span style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 4 }}>
              Aceita variáveis globais e {"{{ lead.follow_count }}"} (follows já enviados a este lead). Digite {"{{"} para ver a lista.
            </span>
          </div>
        </>

      <button onClick={() => mut.mutate()} disabled={mut.isPending || spacingsInvalid}
        style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", fontSize: 13, cursor: spacingsInvalid ? "not-allowed" : "pointer", alignSelf: "flex-start", opacity: spacingsInvalid ? 0.5 : 1 }}>
        {mut.isPending ? "Salvando..." : "Salvar"}
      </button>
      <SavedNote show={mut.isSuccess} />

      {editing?.kind === "template" && (
        <TemplatePopup
          title={`Template · Follow ${editing.i + 1}`}
          initial={slots[editing.i].template}
          approved={approved}
          connected={!!bound?.id}
          onSave={(t) => { patchSlot(editing.i, { template: t }); setEditing(null); }}
          onDelete={() => { patchSlot(editing.i, { template: null }); setEditing(null); }}
          onClose={() => setEditing(null)}
        />
      )}
      {editing?.kind === "prompt" && (
        <PromptPopup
          title={`Prompt extra · Follow ${editing.i + 1}`}
          initial={slots[editing.i].prompt}
          onSave={(p) => { patchSlot(editing.i, { prompt: p }); setEditing(null); }}
          onDelete={() => { patchSlot(editing.i, { prompt: "" }); setEditing(null); }}
          onClose={() => setEditing(null)}
        />
      )}

      {endToolPicker && (
        <div
          {...backdropClose(() => { setEndToolPicker(false); })}
          style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <EscClose onEsc={() => setEndToolPicker(false)} />
          <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 12, width: 360 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Tool de encerramento</span>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {([
                ["Importar", { kind: "http", step: "curl" }],
                ["HTTP", { kind: "http", step: "form" }],
                ["Python", { kind: "python", step: "form" }],
                ["Usar existente", { kind: "http", step: "form" }],
              ] as const).map(([label, cfg]) => (
                <button
                  key={label}
                  onClick={() => {
                    setEndToolPicker(false);
                    if (label === "Importar") setEndToolImporting(true);
                    else if (label === "Usar existente") setEndToolCopying(true);
                    else setEndToolCreating(cfg);
                  }}
                  style={{ flex: 1, background: "var(--surface-input)", color: "var(--ink)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "16px 0", fontSize: 13, cursor: "pointer", transition: "border-color .12s, background .12s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--brand)"; e.currentTarget.style.background = "var(--brand-soft)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.background = "var(--surface-input)"; }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {endToolCreating && (
        <ToolModal
          agentId={agent.id}
          role="followup_end"
          channel={channel}
          initialKind={endToolCreating.kind}
          initialStep={endToolCreating.step}
          onClose={() => {
            setEndToolCreating(null);
            setEndToolEnabled(true);
            qc.invalidateQueries({ queryKey: ["agent-tools", agent.id, "followup_end"] });
          }}
        />
      )}
      {endToolEditing && endTool && (
        <ToolModal
          key={endTool.id}
          agentId={agent.id}
          role="followup_end"
          tool={endTool}
          onClose={() => {
            setEndToolEditing(false);
            qc.invalidateQueries({ queryKey: ["agent-tools", agent.id, "followup_end"] });
          }}
        />
      )}
      {endToolImporting && (
        <TemplatePickerModal
          agentId={agent.id}
          role="followup_end"
          channel={channel}
          onClose={() => {
            setEndToolImporting(false);
            setEndToolEnabled(true);
            qc.invalidateQueries({ queryKey: ["agent-tools", agent.id, "followup_end"] });
          }}
        />
      )}
      {endToolCopying && (
        <div
          {...backdropClose(() => { setEndToolCopying(false); })}
          style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <EscClose onEsc={() => setEndToolCopying(false)} />
          <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 12, width: 420, maxHeight: "70vh" }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Usar tool existente</span>
            <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
              Cria uma cópia independente da tool como tool de encerramento — editar uma não afeta a outra.
            </span>
            <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
              {agentTools.length === 0 ? (
                <span style={{ fontSize: 12, color: "var(--ink-soft)", padding: 12, textAlign: "center" }}>Este agente não tem tools.</span>
              ) : agentTools.map((t: any) => (
                <button key={t.id} disabled={copyEndToolMut.isPending}
                  onClick={() => copyEndToolMut.mutate(t.id)}
                  style={{ textAlign: "left", background: "var(--surface-input)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "10px 12px", cursor: "pointer", display: "flex", flexDirection: "column", gap: 3 }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--brand)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line-strong)"; }}
                >
                  <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{t.name}</span>
                  {t.description && <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>{t.description}</span>}
                  <span style={{ fontSize: 11, color: "var(--line-max)", fontFamily: "var(--font-mono)" }}>
                    {t.kind === "python" ? "Python" : `${t.method ?? ""} ${t.url ?? ""}`}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {confirmDelEndTool && endTool && (
        <ConfirmDialog
          title={`Excluir "${endTool.name}"?`}
          message="A tool de encerramento será removida. Você poderá criar outra do zero."
          confirmLabel="Excluir"
          danger
          onConfirm={() => deleteEndToolMut.mutate()}
          onCancel={() => setConfirmDelEndTool(false)}
        />
      )}
    </div>
  );
}

/** Cartão arrastável de um bloco (Template / Prompt extra): clique edita, arrasta troca de follow. */
function FollowCard({ label, text, onOpen, onDragStart, onDragEnd }: {
  label: string; text: string; onOpen: () => void; onDragStart: () => void; onDragEnd: () => void;
}) {
  return (
    <button type="button" draggable onClick={onOpen}
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", label); onDragStart(); }}
      onDragEnd={onDragEnd}
      title="Clique para editar · arraste para outro follow"
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--brand)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line-strong)"; }}
      style={{
        flex: "1 1 180px", minWidth: 0, textAlign: "left", display: "flex", flexDirection: "column", gap: 2,
        padding: "8px 12px", borderRadius: 8, border: "1px solid var(--line-strong)",
        background: "var(--surface-input)", cursor: "grab", transition: "border-color .12s",
      }}>
      <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--ink-muted)" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{text}</span>
    </button>
  );
}

function AddCardButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--brand)"; e.currentTarget.style.color = "var(--brand)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.color = "var(--ink-muted)"; }}
      style={{
        flex: "1 1 180px", padding: "12px", borderRadius: 8, border: "1px dashed var(--line-strong)", background: "transparent",
        color: "var(--ink-muted)", fontSize: 12, cursor: "pointer", transition: "border-color .12s, color .12s",
      }}>
      {children}
    </button>
  );
}

/** Casca das popups de bloco: Salvar aplica no rascunho da tela; fechar com alteração pede descarte. */
function Popup({ title, dirty, canSave, onSave, onDelete, onClose, children }: {
  title: string; dirty: boolean; canSave: boolean; onSave: () => void; onDelete?: () => void;
  onClose: () => void; children: React.ReactNode;
}) {
  const tryClose = () => (dirty ? askDiscard(onClose) : onClose());
  const btn: React.CSSProperties = { fontSize: 13, padding: "7px 16px", borderRadius: 8, cursor: "pointer" };
  return (
    <div {...backdropClose(tryClose, { guarded: true })}
      style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <EscClose onEsc={tryClose} />
      <div role="dialog" aria-modal="true" aria-label={title}
        style={{ background: "var(--surface-raised)", border: "1px solid var(--line)", borderRadius: 12, boxShadow: "var(--shadow-modal)", width: "min(640px, 100%)", maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--line)", fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>{title}</div>
        <div style={{ padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--line)", display: "flex", alignItems: "center", gap: 8 }}>
          {onDelete && (
            <button type="button" onClick={onDelete}
              style={{ ...btn, border: "1px solid var(--brand-line)", background: "transparent", color: "var(--danger)" }}>
              Excluir
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button type="button" onClick={tryClose}
            style={{ ...btn, border: "1px solid var(--line-strong)", background: "transparent", color: "var(--ink-secondary)" }}>
            Cancelar
          </button>
          <button type="button" onClick={onSave} disabled={!canSave}
            style={{ ...btn, border: "none", background: "var(--brand)", color: "#fff", opacity: canSave ? 1 : 0.5, cursor: canSave ? "pointer" : "not-allowed" }}>
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

/** Template do bloco (sempre enviado) — mesma edição do antigo "Template fora da janela de 24h". */
function TemplatePopup({ title, initial, approved, connected, onSave, onDelete, onClose }: {
  title: string; initial: Tpl | null; approved: any[]; connected: boolean;
  onSave: (t: Tpl) => void; onDelete: () => void; onClose: () => void;
}) {
  const [tplName, setTplName] = useState<string>(initial?.name ?? "");
  const [tplLang, setTplLang] = useState<string>(initial?.language ?? "pt_BR");
  const [tplParams, setTplParams] = useState<TplParam[]>(Array.isArray(initial?.params) ? initial.params : []);
  const [start] = useState(() => JSON.stringify([tplName, tplLang, tplParams]));
  const dirty = JSON.stringify([tplName, tplLang, tplParams]) !== start;
  const setTplParam = (i: number, v: TplParam) => setTplParams(Array.from({ length: Math.max(tplParams.length, i + 1) }, (_, j) => (j === i ? v : tplParams[j] ?? "")));
  const selectedTpl = approved.find((t) => t.name === tplName && t.language === tplLang) ?? approved.find((t) => t.name === tplName);
  const tplBody = bodyOf(selectedTpl);
  const tplSlots = countSlots(tplBody);
  function pickTemplate(v: string) {
    const t = approved.find((x) => `${x.name}|${x.language}` === v);
    setTplName(t?.name ?? ""); setTplLang(t?.language ?? "pt_BR");
    setTplParams(Array.from({ length: countSlots(bodyOf(t)) }, () => ""));
  }
  // Template fora da lista (não carregou / saiu de aprovado): mantém os campos como estão.
  const save = () => onSave({ name: tplName, language: tplLang, params: selectedTpl ? tplParams.slice(0, tplSlots) : tplParams });
  return (
    <Popup title={title} dirty={dirty} canSave={!!tplName && connected} onSave={save}
      onDelete={initial ? onDelete : undefined} onClose={onClose}>
      <span style={{ fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.5 }}>
        Janela aberta: o follow sai pelo prompt (global + extra do bloco). Fechada: envia este template da conta WhatsApp do cliente. Campo Fixo = valor ou {"{{ variável }}"}, sem IA. Campo Agente = você escreve o que ele deve preencher e o agente extrai do histórico (uma chamada de IA).
      </span>
      {!connected ? (
        <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>Agente sem conexão WhatsApp vinculada (aba Conexão).</span>
      ) : (
        <>
          <div>
            <label style={labelStyle}>Template aprovado</label>
            <select style={inputStyle} value={tplName ? `${tplName}|${tplLang}` : ""} onChange={(e) => pickTemplate(e.target.value)}>
              <option value="">— selecione —</option>
              {approved.map((t) => <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>{t.name} ({t.language})</option>)}
            </select>
          </div>
          {tplBody && <div style={{ fontSize: 12, color: "var(--ink-secondary)", whiteSpace: "pre-wrap", padding: "8px 10px", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 8 }}>{tplBody}</div>}
          {Array.from({ length: tplSlots }, (_, i) => {
            const cur = tplParams[i] ?? "";
            const isAi = typeof cur === "object";
            const seg = (
              <div style={{ display: "flex", border: "1px solid var(--line-strong)", borderRadius: 6, overflow: "hidden" }}>
                {([["Agente", true], ["Fixo", false]] as const).map(([lbl, ai]) => (
                  <button key={lbl} type="button"
                    onClick={() => { if (ai !== isAi) setTplParam(i, ai ? { ai: typeof cur === "string" ? cur : "" } : (cur as { ai: string }).ai); }}
                    style={{ fontSize: 10, padding: "2px 9px", cursor: "pointer", border: "none",
                      background: ai === isAi ? "var(--brand)" : "transparent", color: ai === isAi ? "#fff" : "var(--ink-muted)" }}>
                    {lbl}
                  </button>
                ))}
              </div>
            );
            const head = <span style={{ fontSize: 11, color: "var(--ink-secondary)", fontFamily: "var(--font-mono)" }}>{`{{${i + 1}}}`}</span>;
            return isAi ? (
              <ExpressionEditor key={i} autoGrow minHeight={44} maxHeight={120}
                headerLeft={head} headerActions={seg}
                value={cur.ai} onChange={(v) => setTplParam(i, { ai: v })} />
            ) : (
              <ExpressionEditor key={i} autoGrow minHeight={22} maxHeight={78}
                headerLeft={head} headerActions={seg}
                value={cur as string} onChange={(v) => setTplParam(i, v)} />
            );
          })}
        </>
      )}
    </Popup>
  );
}

/** Prompt extra do bloco: vai abaixo do prompt global do follow-up, só neste follow. */
function PromptPopup({ title, initial, onSave, onDelete, onClose }: {
  title: string; initial: string; onSave: (p: string) => void; onDelete: () => void; onClose: () => void;
}) {
  const [text, setText] = useState(initial);
  return (
    <Popup title={title} dirty={text !== initial} canSave={!!text.trim()}
      onSave={() => onSave(text)} onDelete={initial.trim() ? onDelete : undefined} onClose={onClose}>
      <span style={{ fontSize: 11, color: "var(--ink-muted)", lineHeight: 1.5 }}>
        Vai para a IA logo abaixo do prompt global do follow-up, só neste follow. Use para dar nuances (tom, assunto, oferta). Aceita {"{{ variáveis }}"}.
      </span>
      <textarea autoFocus value={text} onChange={(e) => setText(e.target.value)}
        placeholder="Ex: neste follow, pergunte se ele ainda tem interesse e ofereça agendar uma visita."
        style={{ ...inputStyle, minHeight: 280, resize: "vertical", lineHeight: 1.6, fontFamily: "inherit" }} />
    </Popup>
  );
}
